import React, { useState, useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { DynamicForm } from "@shared/components/DynamicForm";
import { Badge } from "@shared/ui/badge";
import { Eye } from "lucide-react";
import { Button } from "@shared/ui/button";
import { FormField, FormService } from "@shared/services/FormServices";
import { useChoices, useGetFieldForceAssignments } from "@features/wx/fieldForce/hooks";
import { useAreaList, useSubAreaList } from "@shared/api/hooks"; // adjust import path as needed
import { PAGE_SIZE } from "@shared/utils/constants";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { useNavigate } from "react-router-dom";

interface ServiceOrdersProps {
  ff_no: string;
}

const ServiceOrders = ({ ff_no }: ServiceOrdersProps) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [areaSelected, setAreaSelected] = useState<string>("");
  const [activeFilters, setActiveFilters] = useState({
    priority: "",
    area: "",
    subArea: "",
  });

  // API hooks
  const {
    data: assignmentsResponse,
    isLoading,
    error,
  } = useGetFieldForceAssignments(ff_no, currentPage, searchTerm, activeFilters);

  const { data: areasList } = useAreaList();
  const { data: subAreasList } = useSubAreaList(areaSelected);
  const { data: priorities } = useChoices('priority');
  const priorityChoices = mapToLabelValue(priorities?.result);
  // Build filter field options from API data
  const areaOptions = useMemo(
    () =>
      (areasList ?? []).map((a: any) => ({
        value: a.id ?? a.value ?? a.name,
        label: a.name ?? a.label,
      })),
    [areasList]
  );

  const subAreaOptions = useMemo(
    () =>
      (subAreasList ?? []).map((s: any) => ({
        value: s.id ?? s.value ?? s.name,
        label: s.name ?? s.label,
      })),
    [subAreasList]
  );

  // DynamicForm filter fields
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "priority",
        label: "Priority",
        type: "select",
        placeholder: "All Priorities",
        options: priorityChoices,
        className: "col-span-1",
      },
      {
        name: "area",
        label: "Area",
        type: "select",
        placeholder: "All Areas",
        options: areaOptions,
        className: "col-span-1",
      },
      {
        name: "subArea",
        label: "Sub Area",
        type: "select",
        placeholder: "All Sub Areas",
        options: subAreaOptions,
        className: "col-span-1",
      },
    ],
    [areaOptions, subAreaOptions, areaSelected, priorityChoices]
  );

  const filterForm = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const handleFilterSubmit = (data: any) => {
    setActiveFilters({
      priority: data.priority || "",
      area: data.area || "",
      subArea: data.subArea || "",
    });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    filterForm.reset();
    setAreaSelected("");
    setActiveFilters({ priority: "", area: "", subArea: "" });
    setCurrentPage(1);
  };

  const handleSearch = (query: string) => {
    setSearchTerm(query);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Derive table data & pagination from API response
  const serviceOrders = assignmentsResponse?.results ?? [];
  const totalCount = assignmentsResponse?.count ?? 0;

  const handleViewServiceOrder = (row: any) => {
    const association = row.sopAssociationTypeDisplay?.toLowerCase();

    switch (association) {
      case "meter":
        navigate(
          `/service-orders/dispatcher/pending-meter-service-order/${row.id}`
        );
        break;
      case "consumer":
        navigate(
          `/service-orders/dispatcher/pending-consumer-service-order/${row.id}`
        );
        break;
      case "asset":
        navigate(
          `/service-orders/dispatcher/pending-asset-service-order/Asset/${row.id}`
        );
        break;
      case "facility":
        navigate(
          `/service-orders/dispatcher/pending-asset-service-order/Facility/${row.id}`
        );
        break;
      case "system":
        navigate(
          `/service-orders/dispatcher/pending-asset-service-order/System/${row.id}`
        );
        break;
      case "network":
        navigate(
          `/service-orders/dispatcher/pending-asset-service-order/Network/${row.id}`
        );
        break;
      default:
        navigate(
          `/service-orders/dispatcher/pending-service-order/${row.id}`
        );
        break;
    }
  };

  const columns = [
    {
      key: "jobCardNo",
      header: "SO ID",
      sortable: true,
    },
    {
      key: "sopName",
      header: "SO Name",
      sortable: true,
    },
    {
      key: "priorityDisplay",
      header: "Priority",
      sortable: true,
      render: (value: string) => {
        return <Badge className={getStatusBadgeClass(value)}>{value}</Badge>;
      },
    },
    {
      key: "area",
      header: "Area",
      sortable: true,
    },
    {
      key: "subArea",
      header: "Sub Area",
      sortable: true,
    },
    {
      key: "assignDate",
      header: "Assigned On",
      sortable: true,
    },
    {
      key: "actions",
      header: "Action",
      render: (_: any, row: any) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleViewServiceOrder(row)}
        >
          <Eye className="w-4 h-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-gray-900">Service Orders</h2>

        <GenericTable
          data={serviceOrders}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            currentPage,
            totalPages: Math.ceil(totalCount / PAGE_SIZE),
            totalItems: totalCount,
            itemsPerPage: PAGE_SIZE,
            startItem: (currentPage - 1) * PAGE_SIZE + 1,
            endItem: Math.min(currentPage * PAGE_SIZE, totalCount),
          }}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          searchPlaceholder="Search service orders..."
          filterPanel={
            <div className="space-y-4">
              <DynamicForm
                fields={filterFields}
                form={filterForm}
                onSubmit={handleFilterSubmit}
                gridColumns={3}
                config={{
                  layout: "vertical",
                  showSubmitButton: false,
                  showResetButton: false,
                  classes: {
                    form: "grid",
                    fieldContainer: "w-full",
                  },
                }}
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleClearFilters}>
                  Clear Filter
                </Button>
                <Button onClick={() => handleFilterSubmit(filterForm.getValues())}>
                  Apply Filter
                </Button>
              </div>
            </div>
          }
          emptyMessage={
            error
              ? "Failed to load service orders. Please try again."
              : "No service orders found matching the criteria."
          }
        />
      </div>
    </div>
  );
};

export default ServiceOrders;